import React from 'react';
import {
  Body,
  Button,
  Group,
  Icon,
  Input,
  Label,
  Select,
  Stack,
  Tooltip,
} from '@qred/components-library';
import useTranslate from '~/hooks/useTranslate';
import { Controller, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { RootState } from '~/store/types/sharedTypes';
import useCountries from '~/hooks/useCountries';
import AddressNL from '../AddressNL/AddressNL';
import {
  IOnboardingAMLData,
  IOnboardingBeneficialOwner,
} from '~/interfaces/Onboarding';
import ValidationErrorMessage from '~/components/shared/ValidationErrorMessage/ValidationErrorMessage';
import { CountryCode } from '@qred/shared-enums';
import { AddressSearch } from '~/components/shared/AddressSearch/AddressSearch';

interface AMLFieldsProps {
  trackFieldChange: (fieldName: string) => void;
  showAddressFields?: boolean;
  hideFields?: string[];
}

const AMLFields = ({
  trackFieldChange,
  showAddressFields,
  hideFields,
}: AMLFieldsProps) => {
  const {
    matchedMedia: { mobile },
    intl: { market },
  } = useSelector((state: RootState) => state);

  const { control, setValue, formState, register } = useFormContext<
    IOnboardingAMLData & {
      address: IOnboardingBeneficialOwner['address'];
      addressFieldValue?: string;
    }
  >();

  const translate = useTranslate();

  const { countriesList } = useCountries();

  if (market === CountryCode.DE && showAddressFields) {
    register('address', {
      required: true,
    });
  }

  return (
    <Stack spacing="md">
      {!hideFields?.includes('countryOfResidence') && (
        <Controller
          name="countryOfResidence"
          control={control}
          rules={{ required: translate('ValidationErrors.Required') as string }}
          render={({ field, fieldState }) => (
            <Select
              label={
                <Label size="md">{translate('AML.CountryOfResidence')}</Label>
              }
              placeholder={translate('AML.SelectCountryPlaceholder') as string}
              options={countriesList}
              onChange={(value) => {
                trackFieldChange(field.name);
                field.onChange(value?.value || '');
              }}
              value={countriesList.find((item) => item.value === field.value)}
              error={fieldState.error?.message}
            />
          )}
        />
      )}

      {!hideFields?.includes('countryOfTaxResidence') && (
        <Controller
          name="countryOfTaxResidence"
          control={control}
          rules={{ required: translate('ValidationErrors.Required') as string }}
          render={({ field, fieldState }) => (
            <Select
              label={
                <Group justify="space-between">
                  <Label size="md">
                    {translate('AML.CountryOfTaxResidence')}
                  </Label>
                  <Tooltip
                    label={translate('AML.CountryOfTaxResidenceTooltip')}
                    events={{
                      focus: false,
                      touch: true,
                      hover: true,
                    }}
                    width={mobile ? '87.2%' : '350px'}
                    position="top-end"
                  >
                    <Icon src="QuestionMarkCircle" size="lg" />
                  </Tooltip>
                </Group>
              }
              placeholder={translate('AML.SelectCountryPlaceholder') as string}
              options={countriesList}
              onChange={(value) => {
                trackFieldChange(field.name);
                field.onChange(value?.value || '');
              }}
              value={countriesList.find((item) => item.value === field.value)}
              error={fieldState.error?.message}
            />
          )}
        />
      )}

      {showAddressFields && (
        <>
          {market === CountryCode.NL && (
            <AddressNL trackFieldChange={trackFieldChange} />
          )}

          {market === CountryCode.DE && (
            <Controller
              name="addressFieldValue"
              control={control}
              rules={{
                required: translate('ValidationErrors.Required') as string,
              }}
              render={({ field, fieldState }) => {
                let defaultValue;
                if (formState.defaultValues?.address) {
                  const {
                    city,
                    streetName,
                    houseNumber,
                  } = formState.defaultValues.address;
                  defaultValue = `${city}, ${streetName} ${houseNumber}`.trim();
                }

                return (
                  <AddressSearch
                    label={translate('Onboarding.ApplicantAddress')}
                    handleResult={(selectedAddress) => {
                      if (selectedAddress?.address) {
                        const _address: IOnboardingBeneficialOwner['address'] = {
                          city: selectedAddress.address.locality,
                          houseNumber: selectedAddress.address.building,
                          streetName: selectedAddress.address.street,
                          zipCode: selectedAddress.address.postcode,
                        };
                        setValue('address', _address, { shouldValidate: true });
                      }
                    }}
                    onChange={(value) => {
                      setValue('address', undefined, { shouldValidate: true });
                      field.onChange(value);
                    }}
                    onBlur={field.onBlur}
                    error={
                      fieldState.error?.message ||
                      (formState.errors.address &&
                        translate(
                          'ValidationErrors.NotSelectedFromAddressSearchResults'
                        ))
                    }
                    initialValue={defaultValue}
                  />
                );
              }}
            />
          )}
        </>
      )}

      {!hideFields?.includes('isPEP') && (
        <Input.Wrapper>
          <Stack spacing="md">
            <Group justify="space-between">
              <Body size="lg">{translate('AML.IsPEPOrRCA')}</Body>
              <Tooltip
                label={translate('AML.IsPEPOrRCATooltip')}
                events={{
                  focus: false,
                  touch: true,
                  hover: true,
                }}
                width={mobile ? '87.2%' : '350px'}
                position="top-end"
              >
                <Icon src="QuestionMarkCircle" size="lg" />
              </Tooltip>
            </Group>
            <Controller
              name="isPEP"
              control={control}
              rules={{ validate: (value) => value !== undefined }}
              render={({ field, fieldState }) => (
                <>
                  <Group grow justify="space-between">
                    <Button
                      variant="secondary"
                      data-active={!!field.value}
                      onClick={() => {
                        trackFieldChange(field.name);
                        field.onChange(true);
                      }}
                      leftIcon={field.value ? 'check' : undefined}
                    >
                      {translate('Yes')}
                    </Button>
                    <Button
                      variant="secondary"
                      data-active={field.value === false}
                      onClick={() => {
                        trackFieldChange(field.name);
                        field.onChange(false);
                      }}
                      leftIcon={field.value === false ? 'check' : undefined}
                    >
                      {translate('No')}
                    </Button>
                  </Group>
                  {field.value && (
                    <Group nowrap spacing="sm">
                      <Icon
                        src="ExclamationTriangle"
                        size="xl"
                        color="warning.700"
                      />
                      <Body size="sm" color="primary.500">
                        {translate('AML.IsPEPOrRCAWarning')}
                      </Body>
                    </Group>
                  )}
                  {fieldState.error && (
                    <ValidationErrorMessage>
                      {translate('ValidationErrors.Required')}
                    </ValidationErrorMessage>
                  )}
                </>
              )}
            />
          </Stack>
        </Input.Wrapper>
      )}
    </Stack>
  );
};

export default AMLFields;
